// @ts-strict-ignore
import { FavoriteArticleVO } from '../../models/favorite-article.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { StringUtils } from '@shared/util/string-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { ArticleNumberMappingUtils } from '@article/util/article-number-mapping-utils';
import { ArticleNumberMappingVO } from '@article/model/vo/article-number-mapping.vo';
import { UserModel } from '@user/model/user.model';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ArticlePreviewUtils } from '@article/util/article-preview.utils';

export class FavoriteArticlesOrderNavigatorContent {
  public tabHeaderText: string = '';

  public isFunctionDefinitionType: boolean = false;

  public isArticlesSortable: boolean = false;
  public isArticlesDeletable: boolean = false;
  public isMoveUpOperationAvailable: boolean = false;
  public isMoveDownOperationAvailable: boolean = false;

  private _favoriteArticles: FavoriteArticleVO[] = [];
  private _removedFavoriteArticleIds: number[] = [];

  private _selectedIndex: number;

  public get selectedIndex(): number {
    return this._selectedIndex;
  }

  public get favoriteArticles(): FavoriteArticleVO[] {
    return this._favoriteArticles;
  }

  public set favoriteArticles(favoriteArticles: FavoriteArticleVO[]) {
    const sortedFavoriteArticles: FavoriteArticleVO[] = ArrayUtils.isNotEmpty(favoriteArticles) ? favoriteArticles.concat() : [];
    sortedFavoriteArticles.sort(this.isArticlesSortable ? this.favoriteArticleOrderCompareFunction : this.favoriteArticleNumberCompareFunction);
    this._favoriteArticles = sortedFavoriteArticles;
  }

  public get removedFavoriteArticleIds(): number[] {
    return this._removedFavoriteArticleIds;
  }

  constructor(
    private localeResourceManager: LocaleResourceManager,
    private userModel: UserModel,
    isArticlesSortable: boolean = false,
    isArticlesDeletable: boolean = false,
    favoriteArticles: FavoriteArticleVO[] = []
  ) {
    this.isArticlesSortable = isArticlesSortable;
    this.isArticlesDeletable = isArticlesDeletable;
    this.favoriteArticles = favoriteArticles;
  }

  private favoriteArticleOrderCompareFunction(a: FavoriteArticleVO, b: FavoriteArticleVO): number {
    if (a.sortOrder === b.sortOrder) {
      return 0;
    } else if (a.sortOrder > b.sortOrder) {
      return 1;
    } else {
      return -1;
    }
  }

  private favoriteArticleNumberCompareFunction(a: FavoriteArticleVO, b: FavoriteArticleVO): number {
    return StringUtils.stringCompare(a.articleNumber, b.articleNumber);
  }

  public getSortedFavoriteArticles(): FavoriteArticleVO[] {
    const sortedFavoriteArticles: FavoriteArticleVO[] = [];
    let favoriteArticle: FavoriteArticleVO = null;
    for (let i = 0; i < this._favoriteArticles.length; i++) {
      favoriteArticle = this._favoriteArticles[i];
      favoriteArticle.sortOrder = i;
      sortedFavoriteArticles.push(favoriteArticle);
    }
    return sortedFavoriteArticles;
  }

  public moveFavoriteArticleUp(): void {
    if (!this.isMoveUpOperationAvailable) {
      return;
    }
    const selectedIndex: number = this.selectedIndex;
    this.moveFavoriteArticle(selectedIndex, selectedIndex - 1);
  }

  public moveFavoriteArticleDown(): void {
    if (!this.isMoveDownOperationAvailable) {
      return;
    }
    const selectedIndex: number = this.selectedIndex;
    this.moveFavoriteArticle(selectedIndex, selectedIndex + 1);
  }

  private moveFavoriteArticle(sourceIndex: number, targetIndex: number): void {
    if (sourceIndex < 0 || sourceIndex >= this.favoriteArticles.length || targetIndex < 0 || targetIndex >= this.favoriteArticles.length) {
      return;
    }
    const itemToSelect: FavoriteArticleVO = this.favoriteArticles.splice(sourceIndex, 1)[0];
    this.favoriteArticles.splice(targetIndex, 0, itemToSelect);
    this.selectIndex(targetIndex);
  }

  public updateSortOperationsAvailability(selectedFavoriteArticle: FavoriteArticleVO): void {
    if (!this.isArticlesSortable) {
      return;
    }

    let moveUpEnabled: boolean = false;
    let moveDownEnabled: boolean = false;
    if (selectedFavoriteArticle != null && this.favoriteArticles.length > 1) {
      const favoriteArticlePosition: number = this.favoriteArticles.indexOf(selectedFavoriteArticle);
      if (favoriteArticlePosition > 0) {
        moveUpEnabled = true;
      }
      if (favoriteArticlePosition < this.favoriteArticles.length - 1) {
        moveDownEnabled = true;
      }
    }
    this.isMoveUpOperationAvailable = moveUpEnabled;
    this.isMoveDownOperationAvailable = moveDownEnabled;
  }

  public showArticleTechnicalInfo(favoriteArticle: FavoriteArticleVO): void {
    if (!favoriteArticle) {
      return;
    }
    ArticlePreviewUtils.openShopLink(favoriteArticle.articleNumber, this.localeResourceManager);
  }

  public removeFavoriteArticle(favoriteArticle: FavoriteArticleVO): void {
    if (!this.isArticlesDeletable || !favoriteArticle) {
      return;
    }
    if (NumberUtils.isNotNumber(favoriteArticle.articleId) && this._removedFavoriteArticleIds.indexOf(favoriteArticle.articleId) < 0) {
      this._removedFavoriteArticleIds.push(favoriteArticle.articleId);
    }
    const removedArticleIndex: number = this._favoriteArticles.indexOf(favoriteArticle);
    if (removedArticleIndex >= 0) {
      if (this._favoriteArticles[this.selectedIndex] === favoriteArticle) {
        this.selectIndex(-1);
      }
      this._favoriteArticles.splice(removedArticleIndex, 1);
    }
  }

  public getCustomNumberArticleByArticleNumber(item: FavoriteArticleVO): string {
    const mapping: ArticleNumberMappingVO = ArticleNumberMappingUtils.findMappingByArticleNumber(
      item.articleNumber,
      this.userModel.articleNumberMappings
    );
    return !mapping ? '' : mapping.customArticleNumber;
  }

  public indexColumnLabelFunction(item: FavoriteArticleVO): string {
    if (!item) {
      return '';
    }
    return String(this.favoriteArticles.indexOf(item) + 1);
  }

  public selectIndex(index: number): void {
    this._selectedIndex = index;

    const selectedFavoriteArticle =
      ArrayUtils.isNotEmpty(this._favoriteArticles) && this._favoriteArticles[this._selectedIndex]
        ? this._favoriteArticles[this._selectedIndex]
        : null;
    this.updateSortOperationsAvailability(selectedFavoriteArticle);
  }
}
